import { useSelector } from 'react-redux';
import { debounce } from '@mui/material';
import { INTEGRATIONS } from '../../components/IntegrationButton';
import supoprtedIntegrationsEsES from '../../integrationsByLocales/es-ES.json';
import supoprtedIntegrationsEsCO from '../../integrationsByLocales/es-CO.json';
import supoprtedIntegrationsEsPE from '../../integrationsByLocales/es-PE.json';
import { useCallback, useMemo, useState } from 'react';
import { CountryCodes } from 'src/shared/utils/currency';
import { selectCompany } from 'src/old/actions/company/companySelectors';
import { selectCompanyIntegrationType } from 'src/old/actions/company/companySelectors';

export const supportedIntegrationsByLocale = {
  [CountryCodes.SPAIN]: supoprtedIntegrationsEsES,
  [CountryCodes.FRANCE]: supoprtedIntegrationsEsES,
  [CountryCodes.ITALY]: supoprtedIntegrationsEsES,
  [CountryCodes.COLOMBIA]: supoprtedIntegrationsEsCO,
  [CountryCodes.PERU]: supoprtedIntegrationsEsPE,
  [CountryCodes.PORTUGAL]: supoprtedIntegrationsEsES
};

const isDisabled =
  (locale: CountryCodes) =>
  (integrationName: INTEGRATIONS): boolean => {
    return !supportedIntegrationsByLocale[locale]?.find(
      supportedIntegrations => supportedIntegrations.id === integrationName
    );
  };

export const useIntegrationsList = () => {
  const currentCompany = useSelector(selectCompany);
  const isDisabledByLocale = isDisabled(currentCompany?.locale);
  const [filteredIntegrations, setFilteredIntegrations] = useState<INTEGRATIONS[] | null>(Object.values(INTEGRATIONS));
  const companyIntegrationType = useSelector(selectCompanyIntegrationType);

  const debouncedHandleSearch = useMemo(() => {
    const handleSearch = (event: any) => {
      if (!event.target.value) {
        setFilteredIntegrations(Object.values(INTEGRATIONS));

        return;
      }

      const filteredAndSorted = filteredIntegrations?.filter((integrationName: string) =>
        integrationName.toLowerCase().includes(event.target.value.toLowerCase())
      );
      setFilteredIntegrations(filteredAndSorted ?? Object.values(INTEGRATIONS));
    };
    return debounce(handleSearch, 300);
  }, [filteredIntegrations, setFilteredIntegrations]);

  const bySupported = useCallback(
    (a: INTEGRATIONS, b: INTEGRATIONS) => {
      if (isDisabledByLocale(a) && !isDisabledByLocale(b)) {
        return 1; // Move 'a' to the end
      } else if (!isDisabledByLocale(a) && isDisabledByLocale(b)) {
        return -1; // Move 'b' to the end
      } else {
        return 0; // Maintain the order
      }
    },
    [isDisabledByLocale]
  );

  const sortedIntegrations = useMemo(() => {
    const { supported, notSupported } = (filteredIntegrations ?? []).reduce<{
      supported: INTEGRATIONS[];
      notSupported: INTEGRATIONS[];
    }>(
      (acc, el) => {
        if (isDisabledByLocale(el)) {
          acc.notSupported.push(el);
        } else {
          acc.supported.push(el);
        }
        return acc;
      },
      { supported: [], notSupported: [] }
    );

    return [...supported.sort(), ...notSupported.sort()];
  }, [filteredIntegrations, isDisabledByLocale]);

  return {
    companyIntegrationType,
    handleSearch: debouncedHandleSearch,
    isDisabledByLocale,
    filteredIntegrations: sortedIntegrations
  };
};
